import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {focusCurrentList} from '../../../common/focusCurrentList.function';
import {t} from '../../../common/getTranslations';
import {getDataset} from '../../../common/shared/utils/getDataset';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {theme} from '../../../theme';
import {Divider} from '../../atoms/Divider/Divider';
import {InfinityScroll} from '../../atoms/InfinityScroll/InfinityScroll';
import {HeaderTitle} from '../../organisms/HeaderTitle/HeaderTitle';
import {MoreExpandList} from '../../organisms/MoreExpandList/MoreExpandList';
import {Pagination} from '../../organisms/Pagination/Pagination';
import {NOTICE_NUM_PER_PAGE} from './NoticeNumPerPage.constant';
import {NoticeProps} from './NoticeProps.interface';
import {NoticeQueryType} from './NoticeQueryType.enum';

export function Notice({
  isWebView,
  isMobile,
  noticeData,
  noticeId,
  noticePage,
  routeToNoticeUrl,
}: NoticeProps) {
  const currentNoticeElement = useRef<HTMLElement>(null);
  const [showContent, setShowContent] = useState(true);
  const [scrollPage, setScrollPage] = useState<number>(noticePage ?? 1);

  const loadNextPage = useCallback(() => {
    if (noticeData) {
      setScrollPage(currentScrollPage => {
        if (currentScrollPage && currentScrollPage < noticeData?.maxPage) {
          return currentScrollPage + 1;
        }
        return currentScrollPage;
      });
    }
  }, [noticeData]);

  const onClickNotice = useCallback(
    (e: React.SyntheticEvent<HTMLElement, Event>) => {
      if (!noticeData) {
        return;
      }
      LOG_CLICK_EVENT('펼치기_클릭', e, () => {
        const id = getDataset(e.currentTarget, 'id');
        if (noticeId !== undefined && noticeId === id) {
          setShowContent(!showContent);
        } else {
          setShowContent(true);
          routeToNoticeUrl(NoticeQueryType.Id, id);
        }
      });
    },
    [noticeData, noticeId, showContent, routeToNoticeUrl]
  );

  const onChangePage = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!noticeData || !noticePage) {
        return;
      }
      const newPage = Number(getDataset(e.currentTarget, 'page'));
      if (noticePage === newPage) {
        return;
      }
      routeToNoticeUrl(NoticeQueryType.Page, newPage);
    },
    [noticeData, noticePage, routeToNoticeUrl]
  );

  useEffect(() => {
    setShowContent(true);
  }, [noticeId]);

  useEffect(() => {
    if (isMobile) {
      focusCurrentList({listElement: currentNoticeElement, isWebView});
    }
  }, [currentNoticeElement, isMobile, isWebView, noticeId, noticePage]);

  useEffect(() => {
    // 뒤로가기시에 스크롤 위치를 기억하는 설정을 manual로 변경
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    return () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
  }, []);

  const NoticeComponent = useMemo(() => {
    if (!noticeData || !noticePage) {
      return null;
    }
    const startIndex = isMobile ? 0 : (noticePage - 1) * NOTICE_NUM_PER_PAGE;
    const endIndex = isMobile
      ? scrollPage * NOTICE_NUM_PER_PAGE
      : startIndex + NOTICE_NUM_PER_PAGE;
    const slicedNotice = noticeData.items.slice(startIndex, endIndex);
    return slicedNotice.map((item, index) => {
      const {id, title, contents, createDt} = item;
      const isActive = noticeId !== undefined && id === noticeId && showContent;
      return (
        <article
          key={id}
          {...putTiaraObjectProps({
            click: {
              ordnum: String(index + 1),
              copy: isActive ? '접기' : '펼치기',
            },
          })}
        >
          <MoreExpandList
            ref={isActive ? currentNoticeElement : null}
            isActive={isActive}
            id={id}
            title={title}
            createDt={createDt}
            contents={contents}
            onClick={onClickNotice}
          />
          <Divider
            color={theme.colors['theme-transparent-10']}
            className="mx-15pxr"
          />
        </article>
      );
    });
  }, [
    scrollPage,
    noticeData,
    noticePage,
    isMobile,
    noticeId,
    showContent,
    onClickNotice,
  ]);

  return (
    <>
      {!isMobile && <HeaderTitle>{t('more_notification')}</HeaderTitle>}
      <main
        className="font-small1 leading-[21px]"
        {...putTiaraObjectProps({
          click: {layer1: '도움말'},
        })}
      >
        {noticeData &&
          (isMobile ? (
            <InfinityScroll
              requestNextPageData={loadNextPage}
              canLoadNextPage={(noticePage ?? 0) < noticeData?.maxPage}
            >
              <dl className="m-0">{NoticeComponent}</dl>
            </InfinityScroll>
          ) : (
            <>
              <dl className="m-0">{NoticeComponent}</dl>
              <Pagination
                currentPage={noticePage}
                onChangePage={onChangePage}
                totalCount={noticeData.totalCount}
              />
            </>
          ))}
      </main>
    </>
  );
}
